'use client'

import { ReactNode } from 'react'
import { usePathname } from 'next/navigation'
import HeaderComponent from '@/app/components/header'
import FooterComponent from '@/app/components/footer'
import SearchBar from '@/app/components/product_search_bar'
import { useLayout } from '@/app/providers/LayoutProvider'

interface BaseLayoutTemplateProps {
  children: ReactNode
  brand?: any
  globalData?: any
  headerMenuJson?: any
  footerJson?: any
  pageInserts?: any
  env?: string
}

export default function BaseLayoutTemplate({
  children,
  brand,
  globalData,
  headerMenuJson,
  footerJson,
  pageInserts,
  env
}: BaseLayoutTemplateProps) {
  const pathname = usePathname()
  const { searchBarOpen, setSearchBarOpen, query, setQuery } = useLayout()

  // Define pages that don't need header/footer (extracted from original layout.jsx)
  const noHeaderFooterList = [
    'login',
    'signup', 
    'admin',
    'amp',
    'successconfirmation', 
    'product3d'
  ]

  // Define pages that need short header only (extracted from original layout.jsx)
  const shortHeaderList = [
    'checkout',
    'payment',
    '404',
    'stylequiz',
    'roomquiz', 
    'recommendations',
    'builder',
    'cart',
    'uploadInstructions'
  ]

  const pathSegments = pathname.split('/').filter(Boolean)
  const firstSegment = pathSegments[0] || ''
  
  // Determine if header/footer should be shown
  const shouldShowHeaderFooter = !pathname.includes('threed') && 
                                !noHeaderFooterList.includes(firstSegment) &&
                                !pathname.startsWith('/admin') &&
                                !pathname.startsWith('/amp')
  
  // Determine header type
  const headerType = shortHeaderList.includes(firstSegment) ? 'short' : 'full'

  // Extract header CMS data from pageInserts
  const headerTopCmsData = pageInserts?.headerTop
  const headerBottomCmsData = pageInserts?.headerBottom

  return (
    <>
      {/* Header */}
      {shouldShowHeaderFooter && (
        <HeaderComponent
          type={headerType}
          brand={brand}
          globalData={globalData}
          headerMenuJson={headerMenuJson}
          headerTopCmsData={headerTopCmsData}
          headerBottomCmsData={headerBottomCmsData}
        />
      )}

      {/* Search Bar - rendered below header when open */}
      {shouldShowHeaderFooter && searchBarOpen && (
        <div style={{ height: '81px' }}>
          <SearchBar
            setSearchBarOpen={setSearchBarOpen}
            query={query}
            setQuery={setQuery}
          />
        </div>
      )}

      {/* Main Content - pageContent wraps the page content */}
      <div className={`pageContent full_width`}>
        {children}
      </div>

      {/* Footer - only show for full layout, not short header */}
      {shouldShowHeaderFooter && headerType === 'full' && (
        <FooterComponent
          brand={brand}
          footerJson={footerJson}
        />
      )}
    </>
  )
}